import request from '@services/request';
import { RemittanceBasicPerson } from '@store/actions/correspondent/types';

const REMITTANCE_FEATURE = 'REMITTANCES';
const REMITTANCE_ENDPOINTS = {
  FEATURE: '/api/v1/corresponsal/remittances/feature',
  BENEFICIARY: '/api/v1/corresponsal/remittances/beneficiary',
  PAYMENT_VALIDATION: '/api/v1/corresponsal/remittances/payment/validation',
  PAYMENT: '/api/v1/corresponsal/remittances/payment',
  PAYMENT_CANCEL: '/api/v1/corresponsal/remittances/payment/cancel',
  PERSON: '/api/v1/corresponsal/remittances/person',
  DAILY_REPORT: '/api/v1/corresponsal/remittances/daily',
  MONTHLY_REPORT: '/api/v1/corresponsal/remittances/monthly',
  DAILY_REPORT_FILE: '/api/v1/corresponsal/remittances/generateDailyReportFile',
  MONTHLY_REPORT_FILE: '/api/v1/corresponsal/remittances/generateMonthlyReportFile',
  PARAMETERS: '/api/v1/corresponsal/remittances/parameters',
  PAYMENT_RECEIPT_DATA: '/api/v1/corresponsal/remittances/payment/receipt-data',
} as const;

interface Beneficiary {
  ci_number: string;
  complement?: string;
}

interface SearchRemittancePayment {
  code: string;
  document: string;
  complement: string;
  phone_number: string;
  remittance_company: string;
}

interface RemittancePayment {
  code: string;
  code_source_funds: number | null;
  code_destination_funds: number | null;
  name_source_funds_other: string | null;
  name_destination_funds_other: string | null;
  code_transaction_reason: number;
  name_company: string | null;
  code_position_level: number | null;
  hire_date: string | null;
  code_relation_remit: number;
}

export interface DailySearchFilter {
  correspondent: number;
  from: string;
  to: string;
  place: string;
  agency: number;
  user: string | number;
}
export interface MonthlySearchFilter {
  correspondent: number;
  place: string;
  agency: number;
  months: string[];
  years: number[];
}

export interface DailyRemittanceReport {
  searchFilters: DailySearchFilter;
  page: number;
  pageSize: number;
}

export interface MonthlyRemittanceReport {
  searchFilters: MonthlySearchFilter;
  page: number;
  pageSize: number;
}

export interface ReprintReceiptRequest {
  request_id: string;
}

type RequestError = {
  response?: {
    status?: number;
  };
  message?: string;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

const postData = async <TBody extends {} | undefined>(url: string, body: TBody) => {
  try {
    const response = await request.post(url, body);
    return response.data;
  } catch (error: unknown) {
    return error;
  }
};

const postBlobData = async <TBody extends {} | undefined>(url: string, body: TBody) => {
  try {
    const response = await request.postBlob(url, body);
    return response.data;
  } catch (error: unknown) {
    return error;
  }
};

const getData = async (url: string) => {
  try {
    const response = await request.get(url);
    return response.data;
  } catch (error: unknown) {
    return error;
  }
};

const handleRateLimitError = (error: unknown) => {
  const requestError = error as RequestError;
  if (requestError?.response?.status === 429) {
    return {
      errors: [
        {
          code: 'RATE_LIMIT_EXCEEDED',
          message: 'Demasiadas solicitudes, espere un momento antes de reintentar',
        },
      ],
      data: null,
    };
  }
  return error;
};

export const getBeneficiary = async (beneficiary: Beneficiary) =>
  postData(REMITTANCE_ENDPOINTS.BENEFICIARY, beneficiary);

export const searchRemittances = async (searchRemittancePayment: SearchRemittancePayment) => {
  try {
    const response = await request.post(REMITTANCE_ENDPOINTS.PAYMENT_VALIDATION, searchRemittancePayment);
    return response.data;
  } catch (error: unknown) {
    return handleRateLimitError(error);
  }
};

export const payRemittance = async (remittancePayment: RemittancePayment) => {
  try {
    const response = await request.post(REMITTANCE_ENDPOINTS.PAYMENT, remittancePayment);
    return response.data;
  } catch (error: unknown) {
    return handleRateLimitError(error);
  }
};

export const cancelRemittancePayment = async (code: string) => {
  try {
    const response = await request.post(REMITTANCE_ENDPOINTS.PAYMENT_CANCEL, { code });
    return response.data;
  } catch (error: unknown) {
    return error;
  }
};

export const createBasicPerson = async (basicPerson: RemittanceBasicPerson) => {
  try {
    const response = await request.post(REMITTANCE_ENDPOINTS.PERSON, basicPerson);
    return response.data;
  } catch (error: unknown) {
    console.error(`[Ganatech - ERROR]: ${getErrorMessage(error)}`);
    return error;
  }
};

export const upsertCorrespondentFeature = async (correspondentId: number, isRemittance: boolean) => {
  const body = {
    correspondent_id: correspondentId,
    enabled: isRemittance,
    feature: REMITTANCE_FEATURE,
  };
  try {
    const result = await request.post(REMITTANCE_ENDPOINTS.FEATURE, body);
    const { data } = result;
    if (data?.errors?.length) {
      const [errorData] = data.errors;
      console.error('[Ganatech - ERROR]: ', errorData);
    }
    return data;
  } catch (error: unknown) {
    console.error(`[Ganatech - ERROR]: ${getErrorMessage(error)}`);
  }
};

export const dailyRemittanceReportService = async (dailyRemittanceReport: DailyRemittanceReport) => {
  return postData(REMITTANCE_ENDPOINTS.DAILY_REPORT, dailyRemittanceReport);
};

export const monthlyRemittanceReportService = async (monthlyRemittanceReport: MonthlyRemittanceReport) => {
  return postData(REMITTANCE_ENDPOINTS.MONTHLY_REPORT, monthlyRemittanceReport);
};

export const dailyRemittancePdfReportService = async (dailyRemittanceReport: DailyRemittanceReport) => {
  return postBlobData(REMITTANCE_ENDPOINTS.DAILY_REPORT_FILE, dailyRemittanceReport);
};

export const monthlyRemittancePdfReportService = async (monthlyRemittanceReport: MonthlyRemittanceReport) => {
  return postData(REMITTANCE_ENDPOINTS.MONTHLY_REPORT_FILE, monthlyRemittanceReport);
};

export const getRemittanceParameters = async () => getData(REMITTANCE_ENDPOINTS.PARAMETERS);

/** Datos del comprobante vía endpoint configurado en REMITTANCE_ENDPOINTS.PAYMENT_RECEIPT_DATA TO_DO */
export const getRemittanceReceiptData = async (reprintRequest: ReprintReceiptRequest) => {
  return postData(REMITTANCE_ENDPOINTS.PAYMENT_RECEIPT_DATA, reprintRequest);
};
